import { useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getApp } from "firebase/app";

import { STAFF_THEME, FONT } from "../../theme/staff";
const BRAND = { ...STAFF_THEME, bg: STAFF_THEME.dark, line: STAFF_THEME.raised };

function toDate(v) {
  if (v == null) return null;
  if (typeof v === "number") return new Date(v);
  if (v.toDate) return v.toDate();
  const d = new Date(v);
  return isNaN(d) ? null : d;
}

function fmtDate(v) {
  const d = toDate(v);
  if (!d) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
    " · " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

// ---------- Composer modal ----------

function ReplyComposerModal({ booking, onClose, onSent }) {
  const log = Array.isArray(booking.emailLog) ? booking.emailLog : [];
  const firstName = (booking.name || "").split(" ")[0];
  const lastSubject = log.length > 0 ? log[log.length - 1].subject : null;

  const [subject, setSubject] = useState(() => {
    if (lastSubject) return lastSubject;
    return "Re: Your Joy of Pancakes inquiry";
  });
  const [message, setMessage] = useState(firstName ? `Hey ${firstName} —\n\n` : "");
  const [includeSummary, setIncludeSummary] = useState(log.length === 0);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);

  const canSend = !sending && subject.trim() && message.trim();

  const onSend = async () => {
    if (!canSend) return;
    setSending(true);
    setErr(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "sendBookingReply");
      const res = await fn({ bookingId: booking.id, subject: subject.trim(), message: message.trim(), includeSummary });
      onSent?.(res.data);
      onClose();
    } catch (e) {
      setErr(e.message || "Send failed");
      setSending(false);
    }
  };

  const fieldStyle = {
    width: "100%", background: BRAND.panel, border: `1px solid ${BRAND.line}`,
    borderRadius: "8px", color: BRAND.text, padding: "0.6rem 0.7rem",
    fontSize: "0.92rem", fontFamily: "inherit", outline: "none", boxSizing: "border-box",
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 50,
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "2rem 1rem", overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: BRAND.bg, border: `1px solid ${BRAND.line}`, borderRadius: "14px",
          padding: "1.1rem", maxWidth: "620px", width: "100%",
          color: BRAND.text, fontFamily: FONT.body,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
          <h2 style={{ margin: 0, fontSize: "1.15rem", color: BRAND.gold }}>✉ Reply to client</h2>
          <button
            type="button" onClick={onClose} aria-label="Close"
            style={{ background: "transparent", border: "none", color: BRAND.muted, fontSize: "1.5rem", cursor: "pointer", lineHeight: 1 }}
          >×</button>
        </div>

        <div style={{ color: BRAND.muted, fontSize: "0.85rem", marginBottom: "0.8rem" }}>
          To: <span style={{ color: BRAND.cream, fontWeight: 700 }}>{booking.name || "Client"}</span>
          {" "}&lt;{booking.email}&gt; · sent from hello@thejoyofpancakes.com on the brand template
        </div>

        {err && (
          <div style={{ background: "#6b2234", color: "white", padding: "0.6rem 0.8rem", borderRadius: "8px", marginBottom: "0.6rem", fontSize: "0.85rem" }}>
            {err}
          </div>
        )}

        <label style={{ display: "block", color: BRAND.muted, fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.05em", marginBottom: "0.25rem" }}>
          SUBJECT
        </label>
        <input
          type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
          disabled={sending} style={{ ...fieldStyle, marginBottom: "0.7rem" }}
        />

        <label style={{ display: "block", color: BRAND.muted, fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.05em", marginBottom: "0.25rem" }}>
          MESSAGE
        </label>
        <textarea
          value={message} onChange={(e) => setMessage(e.target.value)}
          rows={10} disabled={sending} autoFocus
          style={{ ...fieldStyle, resize: "vertical" }}
        />

        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", margin: "0.7rem 0 0.2rem", cursor: "pointer", fontSize: "0.85rem", color: BRAND.cream }}>
          <input
            type="checkbox" checked={includeSummary}
            onChange={(e) => setIncludeSummary(e.target.checked)}
            disabled={sending}
          />
          Quote their inquiry below the message (recommended for the first reply)
        </label>

        <div style={{ color: BRAND.muted, fontSize: "0.75rem", lineHeight: 1.5, margin: "0.5rem 0 0.8rem" }}>
          Their reply lands at jump@thejoyofpancakes.com, and you're BCC'd a copy so the thread lives in your inbox too.
          {booking.status === "new_inquiry" && (
            <> Sending will mark this booking <span style={{ color: BRAND.green, fontWeight: 700 }}>Contacted</span>.</>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
          <button
            type="button" onClick={onClose} disabled={sending}
            style={{
              background: BRAND.panel, color: BRAND.muted, border: `1px solid ${BRAND.line}`,
              borderRadius: "8px", padding: "0.6rem 1rem", fontWeight: 700, fontSize: "0.9rem", cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button" onClick={onSend} disabled={!canSend}
            style={{
              background: canSend ? BRAND.gold : BRAND.panel,
              color: canSend ? BRAND.bg : BRAND.muted,
              border: "none", borderRadius: "8px", padding: "0.6rem 1.2rem",
              fontWeight: 800, fontSize: "0.9rem", cursor: canSend ? "pointer" : "default",
            }}
          >
            {sending ? "Sending…" : "✉ Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Correspondence panel ----------

export default function CorrespondencePanel({ booking, onSent }) {
  const [showComposer, setShowComposer] = useState(false);
  const [openIdx, setOpenIdx] = useState(null);

  const log = Array.isArray(booking.emailLog)
    ? [...booking.emailLog].sort((a, b) => (a.at || 0) - (b.at || 0))
    : [];
  const hasEmail = !!(booking.email || "").trim();
  const fromForm = booking.source === "shopify_form";

  return (
    <div style={{
      background: BRAND.panel, border: `1px solid ${BRAND.line}`, borderRadius: "12px",
      padding: "0.9rem 1rem", marginBottom: "0.9rem",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
        <div style={{ color: BRAND.gold, fontWeight: 800, fontSize: "0.95rem" }}>
          ✉ Correspondence
        </div>
        <button
          type="button"
          onClick={() => hasEmail && setShowComposer(true)}
          disabled={!hasEmail}
          title={hasEmail ? `Email ${booking.email}` : "No client email on this booking"}
          style={{
            background: hasEmail ? BRAND.gold : "rgba(0,0,0,0.25)",
            color: hasEmail ? BRAND.bg : BRAND.muted,
            border: "none", borderRadius: "8px", padding: "0.45rem 0.8rem",
            fontSize: "0.8rem", fontWeight: 800, cursor: hasEmail ? "pointer" : "default",
          }}
        >
          ✉ Reply to client
        </button>
      </div>

      <div style={{ marginTop: "0.6rem" }}>
        {/* Thread root — the inquiry itself */}
        {booking.submittedAt && (
          <div style={{
            display: "flex", gap: "0.5rem", alignItems: "baseline",
            padding: "0.4rem 0", borderBottom: `1px dashed ${BRAND.line}`,
            color: BRAND.muted, fontSize: "0.82rem",
          }}>
            <span>📥</span>
            <span style={{ flex: 1 }}>
              Inquiry received{fromForm ? " via website form" : ""}
            </span>
            <span style={{ whiteSpace: "nowrap" }}>{fmtDate(booking.submittedAt)}</span>
          </div>
        )}

        {log.length === 0 && (
          <div style={{ color: BRAND.muted, fontSize: "0.82rem", padding: "0.5rem 0 0.1rem", fontStyle: "italic" }}>
            No emails sent from the CRM yet.
          </div>
        )}

        {log.map((entry, i) => (
          <div key={i} style={{ borderBottom: i < log.length - 1 ? `1px dashed ${BRAND.line}` : "none" }}>
            <div
              onClick={() => setOpenIdx(openIdx === i ? null : i)}
              style={{
                display: "flex", gap: "0.5rem", alignItems: "baseline",
                padding: "0.45rem 0", cursor: "pointer", fontSize: "0.85rem",
              }}
            >
              <span>{openIdx === i ? "▾" : "▸"}</span>
              <span style={{ flex: 1, color: BRAND.cream, fontWeight: 600, wordBreak: "break-word" }}>
                {entry.subject || "(no subject)"}
              </span>
              <span style={{ color: BRAND.muted, fontSize: "0.78rem", whiteSpace: "nowrap" }}>{fmtDate(entry.at)}</span>
            </div>
            {openIdx === i && (
              <div style={{
                background: "rgba(0,0,0,0.25)", borderRadius: "8px",
                padding: "0.6rem 0.75rem", margin: "0 0 0.55rem 1.1rem",
                color: BRAND.text, fontSize: "0.85rem", whiteSpace: "pre-wrap", lineHeight: 1.55,
              }}>
                {entry.message}
                {entry.includeSummary && (
                  <div style={{ color: BRAND.muted, fontSize: "0.72rem", marginTop: "0.5rem", fontStyle: "italic" }}>
                    + their inquiry summary quoted below
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {showComposer && (
        <ReplyComposerModal booking={booking} onClose={() => setShowComposer(false)} onSent={onSent} />
      )}
    </div>
  );
}
